import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { OpenBriefsApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient.ts";
import { getStartDate } from "@/helpers/dates/getStartDate.ts";
import { selectedClientServiceIdAtom } from "@/store";

import { type BriefFilters } from "@/types/brief";

const apiClient = createApiClient(OpenBriefsApi);

export const useBriefs = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );
  const queryClient = useQueryClient();

  const useGetBriefs = (props?: {
    page: number;
    nbPerPage: number;
    filter?: BriefFilters;
  }) =>
    useSuspenseQuery({
      queryKey: [selectedClientServiceIdAtomValue, "briefs", props],
      queryFn: async () => {
        const response = await apiClient.openBriefsControllerGetBriefs({
          clientServiceId: selectedClientServiceIdAtomValue,
          page: String(props?.page ?? 0),
          nbPerPage: props?.nbPerPage,
          search: props?.filter?.search,
          stopDate: new Date().toISOString(),
          startDate: getStartDate(props?.filter?.period).toISOString(),
        });
        return response.data;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    });

  const useGetBrief = (briefId: string) =>
    useQuery({
      queryKey: [selectedClientServiceIdAtomValue, "briefs", briefId],
      queryFn: async () => {
        const response = await apiClient.openBriefsControllerGetOneBriefById({
          clientServiceId: selectedClientServiceIdAtomValue,
          briefId,
        });
        return response;
      },
      enabled: !!briefId && !!selectedClientServiceIdAtomValue,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });

  const useCreateBrief = () =>
    useMutation({
      mutationFn: (data: { name: string }) => {
        return apiClient.openBriefsControllerCreateBrief({
          clientServiceId: selectedClientServiceIdAtomValue,
          openBriefBriefCreateCommand: data,
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [selectedClientServiceIdAtomValue, "briefs"],
        });
      },
    });

  const useDeleteBrief = () =>
    useMutation({
      mutationFn: (variables: { briefId: string }) => {
        return apiClient.openBriefsControllerDeleteBrief({
          clientServiceId: selectedClientServiceIdAtomValue,
          briefId: variables.briefId,
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: [selectedClientServiceIdAtomValue, "briefs"],
        });
      },
    });

  const useDownloadBriefPdf = (briefId: string) =>
    useMutation({
      mutationFn: async () => {
        const response = await apiClient.openBriefsControllerDownloadBriefPdf(
          {
            clientServiceId: selectedClientServiceIdAtomValue,
            briefId: briefId,
          },
          {
            responseType: "blob",
          }
        );
        return response.data;
      },
    });

  const useSubmitBrief = (briefId: string) =>
    useMutation({
      mutationFn: async ({ comment }: { comment?: string }) => {
        await apiClient.openBriefsControllerSubmitBrief({
          clientServiceId: selectedClientServiceIdAtomValue,
          briefId: briefId,
          openBriefSubmitCommand: comment ? { userComment: comment } : {},
        });
      },
      onSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: [selectedClientServiceIdAtomValue, "briefs"],
          exact: true,
        });
        void queryClient.invalidateQueries(
          {
            queryKey: [selectedClientServiceIdAtomValue, "briefs", briefId],
            exact: true,
          },
          {
            cancelRefetch: false,
          }
        );
      },
    });

  const useGetAssignedUsers = (briefId: string) =>
    useQuery({
      enabled: !!briefId && !!selectedClientServiceIdAtomValue,
      queryKey: ["briefs", briefId, "assignedUsers"],
      queryFn: () =>
        apiClient.openBriefsControllerGetAssignedUsers({
          clientServiceId: selectedClientServiceIdAtomValue,
          briefId,
        }),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });

  const useUpdateAssignedUsers = (briefId: string) => {
    return useMutation({
      mutationKey: ["briefs", briefId, "assignedUser"],
      mutationFn: ({
        userIdsToAdd,
        userIdsToRemove,
      }: {
        userIdsToAdd: string[];
        userIdsToRemove: string[];
      }) =>
        apiClient.openBriefsControllerUpdateAssignedUsers({
          clientServiceId: selectedClientServiceIdAtomValue,
          briefId,
          usersAssignUpdateCommand: {
            add: userIdsToAdd,
            remove: userIdsToRemove,
          },
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries(
            {
              queryKey: ["briefs", briefId],
              exact: true,
            },
            {
              cancelRefetch: false,
            }
          ),
        ]),
    });
  };

  return {
    useGetBriefs,
    useGetBrief,
    useCreateBrief,
    useDeleteBrief,
    useDownloadBriefPdf,
    useSubmitBrief,
    useGetAssignedUsers,
    useUpdateAssignedUsers,
  };
};
